import { useAppDispatch, useAppSelector } from "./../../../../hooks";
import { useEffect, useState } from "react";

import {
  Button,
  EmptyState,
  Loading,
  SecureCopy,
  SimpleTable,
  Typography,
} from "djuno-design";

import {
  handleSynchronisation,
  selectAiNotebook,
  selectAiNotebookLoading,
} from "../../../../store/ai/notebooks/notebookSlice";
import { Helmet } from "react-helmet";
import HighlighterText from "../../../general/HighlighterText";
import { useSearch } from "../../../../providers/SearchProvider";
import { SynchronisationModal } from "./SynchronisationModal";
import { AiVolume } from "../../../../types/ai";

const AiAttachedDataTab = () => {
  const [filteredAiNotbooks, setFilteredAiNotebooks] = useState<AiVolume[]>([]);

  const dispatch = useAppDispatch();
  const { value: searchValue } = useSearch();

  const notebook = useAppSelector(selectAiNotebook);
  const notebookLoading = useAppSelector(selectAiNotebookLoading);

  useEffect(() => {
    if (notebook) {
      setFilteredAiNotebooks(notebook.spec.volumes);
    }
  }, [notebook]);

  if (notebookLoading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>AI Notebooks</title>
        <meta name="description" content="" />
      </Helmet>

      <>
        <div className="flex flex-col gap-3">
          <Typography.Title level={5} className="!mb-0">
            Attach data containers
          </Typography.Title>

          <div className="items-center justify-between flex flex-1 transition duration-150">
            <Typography.Text className="!text-sm ">
              If necessary, you can attach Djuno cloud Object Storage containers
              to your notebook.Once they are attached, they will be temporarily
              loaded and cached near your instance, to minimise latency and
              improve performance. The best practice is to attach one container
              with incoming data, and another with your outgoing data.
            </Typography.Text>
          </div>

          <div className="flex items-center gap-2">
            <Button
              uiType="primary"
              onClick={() => dispatch(handleSynchronisation({ status: true }))}
              className="group"
            >
              Global data synchronisation
            </Button>
          </div>
        </div>

        <div className="mt-5 w-full">
          <SimpleTable
            loading={notebookLoading}
            containerClassName="min-h-[240px]"
          >
            <SimpleTable.Head>
              <SimpleTable.Row>
                <SimpleTable.TH lable="Data container" />
                <SimpleTable.TH lable="Prefix" />
                <SimpleTable.TH lable="Location" />
                <SimpleTable.TH lable="Mount directory" />
                <SimpleTable.TH lable="Permision" />
                <SimpleTable.TH lable="Cashe" />
              </SimpleTable.Row>
            </SimpleTable.Head>
            <SimpleTable.Body>
              {notebook && filteredAiNotbooks.length > 0 ? (
                filteredAiNotbooks
                  .filter((c) => c.container || c.prefix)
                  .map((c, i) => (
                    <SimpleTable.Row key={i}>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.container}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.prefix}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.region}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          <SecureCopy type="copy" text={c.mountPath} />
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.permission}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.cache ? "true" : "false"}
                        </Typography.Text>
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  ))
              ) : (
                <SimpleTable.Row withoutHoverStyle className="h-[200px]">
                  <SimpleTable.TD colSpan={2} className="!border-0">
                    <EmptyState text="No data available" />
                  </SimpleTable.TD>
                </SimpleTable.Row>
              )}
            </SimpleTable.Body>
          </SimpleTable>
        </div>

        <div className="flex flex-col gap-3">
          <Typography.Title level={5} className="!mb-0">
            Attached public Git repositorys
          </Typography.Title>
        </div>
        <div className="mt-5 w-full">
          <SimpleTable
            loading={notebookLoading}
            containerClassName="min-h-[240px]"
          >
            <SimpleTable.Head>
              <SimpleTable.Row>
                <SimpleTable.TH lable="Git repository URL" />
                <SimpleTable.TH lable="Mount directory" />
                <SimpleTable.TH lable="Permission" />
              </SimpleTable.Row>
            </SimpleTable.Head>
            <SimpleTable.Body>
              {notebook && filteredAiNotbooks.length > 0 ? (
                filteredAiNotbooks
                  .filter((c) => c.volumeSource?.publicGit?.url)
                  .map((c, i) => (
                    <SimpleTable.Row key={i}>
                      <SimpleTable.TD>
                        <HighlighterText
                          searchValue={searchValue}
                          textToHighlight={c.id || ""}
                          className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate"
                        />
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.volumeSource?.publicGit?.url}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <SecureCopy type="copy" text={c.mountPath} />
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {c.permission || "N/A"}
                        </Typography.Text>
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  ))
              ) : (
                <SimpleTable.Row className="h-[200px]">
                  <SimpleTable.TD colSpan={3} className="!border-0">
                    <EmptyState text="No associated Git repository" />
                  </SimpleTable.TD>
                </SimpleTable.Row>
              )}
            </SimpleTable.Body>
          </SimpleTable>
        </div>

        <SynchronisationModal />
      </>
    </>
  );
};
export default AiAttachedDataTab;
